/**
 * MqttInterface - MQTT transport layer
 *
 * 브로커 JSON 프로토콜(register/list/connect/disconnect)과
 * protobuf 기반 device protocol 을 단일 MQTT client 에서 처리합니다.
 */
import mqtt, { MqttClient, IClientOptions } from "mqtt";
import { Buffer } from "buffer";
import { Message } from "@brain/proto";
import { Message as MessageCodec } from "@brain/proto/generated/brain_msgs";
import {
  MqttConfig,
  DEFAULT_MQTT_CONFIG,
  mqttTopics,
  BRAIN_TOPIC_TYPE_NAMES,
  BRAIN_TOPICS_CONTROL,
  BRAIN_TOPICS_HEARTBEAT,
  BRAIN_TOPICS_STATUS,
  BRAIN_TOPICS_SYSTEM,
  BrainTopic,
} from "../config/mqtt_config";
import {
  encodeAckInfo,
  extractIncomingHeader,
  decodeBrainPayloadForLog,
  decodeVehicleState,
  extractVehiclePosition,
  decodeControlRequest,
  type DecodedVehiclePosition,
} from "./mqtt_codec";
import {
  isDrivingTopicLogEnabled,
  logDrivingTopic,
} from "../utils/driving_log_sink";

export type BrokerCredentials = {
  userId: string;
  password?: string;
};

/**
 * 함대 차량이 보낸 제어권 요청.
 *
 * 함대 토픽은 위치를 받으려고 이미 전부 구독하고 있다. 다만 지금까지는 STATE 만
 * 통과시키고 나머지는 버렸다 — 그래서 **연결하지 않은 차량의 요청은 도착해도
 * 화면까지 오지 못했다.** 위치와 같은 자리에서 요청도 분배한다.
 */
/** 토픽 형식 `{vehicleId}/to/{userId}` 에서 출발 차량 ID 를 뽑는다. */
function extractSourceVehicleId(mqttTopic: string): string {
  const separatorIndex = mqttTopic.indexOf("/to/");
  if (separatorIndex <= 0) {
    return "";
  }
  return mqttTopic.slice(0, separatorIndex);
}

export type FleetControlRequestHandler = (
  vehicleId: string,
  request: { requestState: number; controllerId: string; remoteType: number },
) => void;

export type FleetPositionHandler = (
  vehicleId: string,
  position: DecodedVehiclePosition,
) => void;

type MessageHandler = (topic: BrainTopic, payload: Uint8Array) => void;
type JsonHandler = (topic: string, payload: any) => void;
type PacketSendHandler = (packet: any) => void;
type PublishedMessageHandler = (topic: BrainTopic, payload: Uint8Array) => void;
export type MqttConnectionState =
  | "connected"
  | "disconnected"
  | "reconnecting"
  | "error";
export type MqttConnectionStateHandler = (
  state: MqttConnectionState,
  error?: Error,
) => void;

const AUTO_ACK_TOPICS = new Set<BrainTopic>([
  BRAIN_TOPICS_STATUS.CONTROL,
  BRAIN_TOPICS_STATUS.CAMERA_PARAMS,
  BRAIN_TOPICS_STATUS.VEHICLE_REQUEST,
]);

function makeTimestamp(): number {
  return Date.now();
}

function formatError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }
  return new Error(String(err));
}

export default class MqttInterface {
  private client: MqttClient | null = null;
  public config: MqttConfig;
  private handlers: Map<BrainTopic, Set<MessageHandler>> = new Map();
  private jsonHandlers: Map<string, Set<JsonHandler>> = new Map();
  private packetSendHandlers: Set<PacketSendHandler> = new Set();
  private publishedMessageHandlers: Set<PublishedMessageHandler> = new Set();
  private connectionStateHandlers: Set<MqttConnectionStateHandler> = new Set();
  private _connected: boolean = false;
  private currentUserId: string | null = null;
  private currentPassword: string | null = null;
  private currentVehicleId: string | null = null;
  private currentVehicleTopic: string | null = null;
  // 전차량 위치 수신(read-only) — vehicleId → 구독한 MQTT 토픽.
  private fleetPositionTopics: Map<string, string> = new Map();
  private fleetPositionHandlers: Set<FleetPositionHandler> = new Set();
  private fleetControlRequestHandlers: Set<FleetControlRequestHandler> =
    new Set();
  /** 함대 토픽에서 처음 본 (차량, brain 토픽) 조합 — 진단 로그를 한 번씩만 찍기 위함. */
  private seenFleetBrainTopics: Set<string> = new Set();

  constructor(config: Partial<MqttConfig> = {}) {
    this.config = { ...DEFAULT_MQTT_CONFIG, ...config };
  }

  get connected(): boolean {
    return this._connected;
  }

  get controllerId(): string {
    return this.currentUserId || this.config.controllerId;
  }

  get userId(): string | null {
    return this.currentUserId;
  }

  get vehicleId(): string | null {
    return this.currentVehicleId;
  }

  connectBroker(credentials: BrokerCredentials): Promise<void> {
    if (!credentials.userId) {
      return Promise.reject(new Error("userId is required"));
    }

    if (
      this.client &&
      this._connected &&
      this.currentUserId === credentials.userId
    ) {
      return Promise.resolve();
    }

    if (this.client) {
      this.disconnect();
    }

    this.currentUserId = credentials.userId;
    this.currentPassword = credentials.password ?? this.config.password;
    this.config.controllerId = credentials.userId;
    this.config.username = credentials.userId;

    const brokerUrl = this.config.brokerWsUrl || this.config.wsUrl;

    return new Promise((resolve, reject) => {
      let settled = false;
      const options: IClientOptions = {
        clientId: `${credentials.userId}-${crypto.randomUUID().slice(0, 8)}`,
        username: credentials.userId,
        password: this.currentPassword || undefined,
        clean: true,
        connectTimeout: 5000,
        reconnectPeriod: 1000,
        keepalive: 60,
      };

      const client = mqtt.connect(brokerUrl, options);
      this.client = client;

      const rejectOnce = (err: unknown) => {
        if (settled) {
          return;
        }
        settled = true;
        reject(formatError(err));
      };

      client.on("message", (topic: string, message: Buffer) => {
        this.handleClientMessage(topic, message);
      });

      client.on("packetsend", (packet: any) => {
        this.packetSendHandlers.forEach((handler) => {
          handler(packet);
        });
      });

      client.on("error", (err) => {
        if (this.client !== client) {
          return;
        }
        console.error("[MqttInterface] Error:", err);
        this.emitConnectionState("error", formatError(err));
        if (!this._connected) {
          rejectOnce(err);
        }
      });

      client.on("close", () => {
        if (this.client !== client) {
          return;
        }
        console.log("[MqttInterface] Disconnected");
        this._connected = false;
        this.emitConnectionState("disconnected");
      });

      client.on("reconnect", () => {
        if (this.client !== client) {
          return;
        }
        this._connected = false;
        this.emitConnectionState("reconnecting");
      });

      client.on("offline", () => {
        if (this.client !== client) {
          return;
        }
        this._connected = false;
        this.emitConnectionState("reconnecting");
      });

      client.on("connect", () => {
        if (this.client !== client) {
          return;
        }
        this._connected = true;
        console.log("[MqttInterface] Connected to broker", brokerUrl);
        this.subscribeActiveTopics(client, credentials.userId)
          .then(() => {
            this.emitConnectionState("connected");
            if (!settled) {
              settled = true;
              resolve();
            }
          })
          .catch((err) => {
            const error = formatError(err);
            console.error("[MqttInterface] Failed to subscribe topics:", error);
            this.emitConnectionState("error", error);
            rejectOnce(error);
          });
      });
    });
  }

  subscribeVehicle(vehicleId: string): Promise<void> {
    const client = this.requireConnectedClient();
    const userId = this.requireUserId();

    if (this.currentVehicleId && this.currentVehicleId !== vehicleId) {
      return Promise.reject(
        new Error(
          `Already connected to vehicle ${this.currentVehicleId}. Disconnect first.`,
        ),
      );
    }

    if (this.currentVehicleId === vehicleId) {
      return Promise.resolve();
    }

    const topic = mqttTopics(vehicleId, userId).vehicleToCockpit;

    return new Promise((resolve, reject) => {
      client.subscribe(topic, { qos: 1 }, (err) => {
        if (err) {
          reject(err);
          return;
        }

        this.currentVehicleId = vehicleId;
        this.currentVehicleTopic = topic;
        this.config.vehicleId = vehicleId;
        resolve();
      });
    });
  }

  unsubscribeVehicle(vehicleId: string): Promise<void> {
    if (!this.client || !this._connected) {
      this.currentVehicleId = null;
      this.currentVehicleTopic = null;
      return Promise.resolve();
    }

    if (!this.currentVehicleId || this.currentVehicleId !== vehicleId) {
      return Promise.resolve();
    }

    const topic =
      this.currentVehicleTopic ||
      mqttTopics(vehicleId, this.requireUserId()).vehicleToCockpit;

    return new Promise((resolve, reject) => {
      this.client!.unsubscribe(topic, (err) => {
        this.currentVehicleId = null;
        this.currentVehicleTopic = null;
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  }

  /**
   * 전차량 위치 수신(read-only) 구독.
   * 각 차량의 vehicleToCockpit 베이스 토픽({vehicleId}/to/{userId}) 을 구독해
   * VehicleState(=STATE brain topic) 의 gnss 위치를 핸들러로 전달한다.
   * 제어 세션(subscribeVehicle)과 독립적으로 동작하며, 이미 구독한 차량은 건너뛴다.
   * 목록에 없는(=더 이상 필요 없는) 차량은 자동으로 unsubscribe 한다.
   */
  setFleetPositionVehicles(vehicleIds: string[]): Promise<void> {
    if (!this.client || !this._connected || !this.currentUserId) {
      return Promise.resolve();
    }

    const client = this.client;
    const userId = this.currentUserId;
    const desired = new Set(vehicleIds.filter(Boolean));

    const subscribePromises: Promise<void>[] = [];
    desired.forEach((vehicleId) => {
      if (this.fleetPositionTopics.has(vehicleId)) {
        return;
      }
      const topic = mqttTopics(vehicleId, userId).vehicleToCockpit;
      this.fleetPositionTopics.set(vehicleId, topic);
      subscribePromises.push(
        new Promise<void>((resolve) => {
          client.subscribe(topic, { qos: 0 }, (err) => {
            if (err) {
              // 구독 실패 — 해당 차량은 위치 없음으로 처리.
              this.fleetPositionTopics.delete(vehicleId);
            }
            resolve();
          });
        }),
      );
    });

    this.fleetPositionTopics.forEach((topic, vehicleId) => {
      if (desired.has(vehicleId)) {
        return;
      }
      this.fleetPositionTopics.delete(vehicleId);
      // 제어 세션이 동일 토픽을 쓰는 경우(현재 연결 차량)는 unsubscribe 하지 않는다.
      if (topic === this.currentVehicleTopic) {
        return;
      }
      client.unsubscribe(topic, () => undefined);
    });

    return Promise.all(subscribePromises).then(() => undefined);
  }

  onFleetControlRequest(handler: FleetControlRequestHandler): () => void {
    this.fleetControlRequestHandlers.add(handler);
    return () => {
      this.fleetControlRequestHandlers.delete(handler);
    };
  }

  onFleetPosition(handler: FleetPositionHandler): () => void {
    this.fleetPositionHandlers.add(handler);
    return () => {
      this.fleetPositionHandlers.delete(handler);
    };
  }

  clearFleetPositions(): void {
    if (this.client && this._connected) {
      this.fleetPositionTopics.forEach((topic) => {
        if (topic === this.currentVehicleTopic) {
          return;
        }
        this.client!.unsubscribe(topic, () => undefined);
      });
    }
    this.fleetPositionTopics.clear();
  }

  disconnect(): void {
    if (this.client) {
      this.client.end(true);
      this.client = null;
    }
    this._connected = false;
    this.currentUserId = null;
    this.currentPassword = null;
    this.currentVehicleId = null;
    this.currentVehicleTopic = null;
    this.fleetPositionTopics.clear();
    this.seenFleetBrainTopics.clear();
  }

  onJsonMessage(topic: string, handler: JsonHandler): () => void {
    const existing = this.jsonHandlers.get(topic) || new Set<JsonHandler>();
    existing.add(handler);
    this.jsonHandlers.set(topic, existing);

    return () => {
      const current = this.jsonHandlers.get(topic);
      if (!current) {
        return;
      }
      current.delete(handler);
      if (current.size === 0) {
        this.jsonHandlers.delete(topic);
      }
    };
  }

  onPacketSend(handler: PacketSendHandler): () => void {
    this.packetSendHandlers.add(handler);

    return () => {
      this.packetSendHandlers.delete(handler);
    };
  }

  onPublish(handler: PublishedMessageHandler): () => void {
    this.publishedMessageHandlers.add(handler);

    return () => {
      this.publishedMessageHandlers.delete(handler);
    };
  }

  onConnectionState(handler: MqttConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);

    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  sendRegister(userId: string): void {
    this.publishJson(`${userId}/register`, {
      messageType: "register",
      timestamp: makeTimestamp(),
      requester: userId,
    });
  }

  sendListRequest(userId: string): void {
    this.publishJson(`${userId}/list`, {
      messageType: "list",
      timestamp: makeTimestamp(),
      requester: userId,
    });
  }

  sendConnectVehicle(userId: string, vehicleId: string): void {
    this.publishJson(`${userId}/connect`, {
      messageType: "connect",
      timestamp: makeTimestamp(),
      userId,
      vehicleId,
    });
  }

  sendDisconnectVehicle(userId: string, vehicleId: string): void {
    this.publishJson(`${userId}/disconnect`, {
      messageType: "disconnect",
      timestamp: makeTimestamp(),
      userId,
      vehicleId,
    });
  }

  sendDisconnectResponse(userId: string, vehicleId: string): void {
    this.publishJson(`${userId}/disconnect/response`, {
      messageType: "disconnect/response",
      timestamp: makeTimestamp(),
      userId,
      vehicleId,
      result: "success",
    });
  }

  publish(brainTopic: BrainTopic, payload: Uint8Array): void {
    const client = this.requireConnectedClient();
    const userId = this.requireUserId();

    if (!this.currentVehicleId) {
      throw new Error("Vehicle is not connected");
    }

    const envelope: Message = {
      topic: brainTopic,
      typeName: BRAIN_TOPIC_TYPE_NAMES[brainTopic] || brainTopic,
      payload,
    };

    const encoded = MessageCodec.encode(envelope).finish();
    const mqttTopic = mqttTopics(
      this.currentVehicleId,
      userId,
    ).cockpitToVehicle;
    client.publish(mqttTopic, Buffer.from(encoded), { qos: 1 });
    this.tapDrivingLog("tx", brainTopic, payload);
    this.publishedMessageHandlers.forEach((handler) => {
      handler(brainTopic, payload);
    });
  }

  onMessage(brainTopic: BrainTopic, handler: MessageHandler): () => void {
    const existing = this.handlers.get(brainTopic) || new Set<MessageHandler>();
    existing.add(handler);
    this.handlers.set(brainTopic, existing);

    return () => {
      const current = this.handlers.get(brainTopic);
      if (!current) {
        return;
      }
      current.delete(handler);
      if (current.size === 0) {
        this.handlers.delete(brainTopic);
      }
    };
  }

  private handleClientMessage(topic: string, message: Buffer): void {
    const userTopicPrefix = this.currentUserId
      ? `${this.currentUserId}/`
      : null;
    const userDirectTopicPrefix = this.currentUserId
      ? `${this.currentUserId}/to/`
      : null;

    if (
      userTopicPrefix &&
      topic.startsWith(userTopicPrefix) &&
      (!userDirectTopicPrefix || !topic.startsWith(userDirectTopicPrefix))
    ) {
      try {
        const parsed = JSON.parse(message.toString());
        const jsonEvtHandlers = this.jsonHandlers.get(topic);
        if (jsonEvtHandlers) {
          jsonEvtHandlers.forEach((handler) => {
            handler(topic, parsed);
          });
        }
      } catch (_err) {
        // user topic 아래 protobuf/direct-control publish echo 는 무시
      }
      return;
    }

    try {
      const envelope = MessageCodec.decode(new Uint8Array(message));
      const brainTopic = envelope.topic as BrainTopic;

      // 전차량 위치 라우팅 — STATE(VehicleState) 메시지면 토픽의 출발 vehicleId 로
      // 위치를 분배한다(제어 세션과 독립). 현재 연결 차량의 STATE 도 함께 전달.
      this.noteFleetBrainTopic(topic, brainTopic);
      this.dispatchFleetPositionIfNeeded(topic, brainTopic, envelope.payload);
      this.dispatchFleetControlRequestIfNeeded(
        topic,
        brainTopic,
        envelope.payload,
      );

      // 제어 세션이 구독한 차량 토픽이 아니면(=fleet-only 토픽) 이후 제어 핸들러 처리는 건너뛴다.
      if (this.currentVehicleTopic && topic !== this.currentVehicleTopic) {
        return;
      }

      this.tapDrivingLog("rx", brainTopic, envelope.payload);

      if (brainTopic === BRAIN_TOPICS_HEARTBEAT.PING) {
        this.sendHeartbeatAck(envelope.payload);
      }

      this.sendEventAckIfNeeded(brainTopic, envelope);

      const topicHandlers = this.handlers.get(brainTopic);
      if (!topicHandlers) {
        return;
      }

      topicHandlers.forEach((handler) => {
        handler(brainTopic, envelope.payload);
      });
    } catch (_err) {
      // protobuf 이 아닌 payload 는 무시
    }
  }

  /**
   * 함대 차량의 제어권 요청을 출발 vehicleId 와 함께 분배한다.
   *
   * 아래 dispatchFleetPositionIfNeeded 와 같은 자리에서 불려야 한다 — 제어 세션 필터
   * (currentVehicleTopic) 보다 앞이라야 연결하지 않은 차량의 요청이 살아남는다.
   */
  /**
   * 함대 차량이 어떤 brain 토픽을 보내는지 조합마다 한 번씩만 남긴다.
   *
   * "차량이 요청을 눌렀는데 콕핏에 안 뜬다"를 볼 때, 메시지가 아예 안 오는 것인지
   * 다른 토픽으로 오는 것인지 구분할 방법이 없었다. 매번 찍으면 STATE(10Hz × 대수)로
   * 콘솔이 묻히므로 처음 한 번만 남긴다.
   */
  private noteFleetBrainTopic(mqttTopic: string, brainTopic: BrainTopic): void {
    if (!this.fleetPositionTopics.has(extractSourceVehicleId(mqttTopic))) {
      return;
    }
    const key = `${mqttTopic}|${brainTopic}`;
    if (this.seenFleetBrainTopics.has(key)) {
      return;
    }
    this.seenFleetBrainTopics.add(key);
    console.log("[FLEET] first message on topic", { mqttTopic, brainTopic });
  }

  private dispatchFleetControlRequestIfNeeded(
    mqttTopic: string,
    brainTopic: BrainTopic,
    payload: Uint8Array,
  ): void {
    if (brainTopic !== BRAIN_TOPICS_STATUS.VEHICLE_REQUEST) {
      return;
    }

    const vehicleId = extractSourceVehicleId(mqttTopic);
    // 항상 켜진 진단 — "차량이 요청을 눌렀는데 콕핏에 안 뜬다"를 가를 수 있어야 한다.
    // 이 줄이 안 찍히면 메시지가 애초에 도착하지 않은 것(토픽/발행 주체 문제),
    // 찍히는데 handlers 가 0 이면 구독 배선 문제다.
    console.log("[FLEET] control request rx", {
      mqttTopic,
      vehicleId,
      handlers: this.fleetControlRequestHandlers.size,
    });

    if (this.fleetControlRequestHandlers.size === 0 || !vehicleId) {
      return;
    }

    try {
      const request = decodeControlRequest(payload);
      this.fleetControlRequestHandlers.forEach((handler) => {
        handler(vehicleId, {
          requestState: Number(request?.requestState ?? 0),
          controllerId: String(request?.controllerId ?? ""),
          remoteType: Number(request?.remoteType ?? 0),
        });
      });
    } catch (_err) {
      // 디코드 실패 — 분배 생략.
    }
  }

  private dispatchFleetPositionIfNeeded(
    mqttTopic: string,
    brainTopic: BrainTopic,
    payload: Uint8Array,
  ): void {
    if (
      this.fleetPositionHandlers.size === 0 ||
      brainTopic !== BRAIN_TOPICS_STATUS.STATE
    ) {
      return;
    }

    const vehicleId = extractSourceVehicleId(mqttTopic);
    if (!vehicleId) {
      return;
    }

    try {
      const vehicleState = decodeVehicleState(payload);
      const position = extractVehiclePosition(vehicleState);
      if (!position) {
        return;
      }
      this.fleetPositionHandlers.forEach((handler) => {
        handler(vehicleId, position);
      });
    } catch (_err) {
      // 디코드 실패 — 위치 분배 생략.
    }
  }

  private publishJson(topic: string, payload: Record<string, unknown>): void {
    const client = this.requireConnectedClient();
    this.requireUserId();
    client.publish(topic, JSON.stringify(payload), { qos: 1 });
  }

  private subscribeActiveTopics(client: MqttClient, userId: string): Promise<void> {
    const topics = [`${userId}/#`];
    if (this.currentVehicleTopic) {
      topics.push(this.currentVehicleTopic);
    }
    // 함대 토픽도 다시 건다.
    //
    // 여기 빠져 있었다 — 브로커가 한 번 끊겼다 붙으면 `${userId}/#` 와 제어 세션
    // 토픽만 복구되고 함대 구독은 조용히 사라진다. 그러면 전차량 위치도, 차량이
    // 올리는 제어권 요청도 더는 도착하지 않는데 화면에는 아무 표시가 없다.
    this.fleetPositionTopics.forEach((topic) => {
      if (!topics.includes(topic)) {
        topics.push(topic);
      }
    });
    console.log("[FLEET] subscribe", { topics });

    return Promise.all(
      topics.map(
        (topic) =>
          new Promise<void>((resolve, reject) => {
            client.subscribe(topic, { qos: 1 }, (err) => {
              if (err) {
                reject(err);
                return;
              }
              resolve();
            });
          }),
      ),
    ).then(() => undefined);
  }

  private emitConnectionState(
    state: MqttConnectionState,
    error?: Error,
  ): void {
    this.connectionStateHandlers.forEach((handler) => {
      handler(state, error);
    });
  }

  private requireConnectedClient(): MqttClient {
    if (!this.client || !this._connected) {
      throw new Error("MQTT broker is not connected");
    }
    return this.client;
  }

  private requireUserId(): string {
    if (!this.currentUserId) {
      throw new Error("MQTT user session is not initialized");
    }
    return this.currentUserId;
  }

  /**
   * 주행 데이터 로깅 탭 — 구독 여부와 무관하게 **모든** 브레인 토픽이 이 한 곳을 지난다.
   *
   * 구독자(useMqttVehicleData) 쪽에 붙이지 않은 이유가 그것이다. 거기서는 UI 가 실제로
   * 쓰는 토픽만 보이고, 한 토픽에 핸들러가 여럿이면 같은 메시지가 여러 줄로 찍힌다.
   *
   * 로깅이 꺼져 있으면 isDrivingTopicLogEnabled 의 불리언 한 번으로 끝난다 —
   * 이 경로는 초당 수백 번 불리므로 디코드는 켜졌을 때만 해야 한다.
   */
  private tapDrivingLog(
    direction: "rx" | "tx",
    brainTopic: BrainTopic,
    payload: Uint8Array,
  ): void {
    try {
      if (!isDrivingTopicLogEnabled(direction, brainTopic)) {
        return;
      }
      logDrivingTopic(direction, brainTopic, decodeBrainPayloadForLog(brainTopic, payload));
    } catch (err) {
      // 로깅이 전송/수신을 멈추게 해선 안 된다.
      console.warn("[MqttInterface] driving log tap failed:", err);
    }
  }

  private sendHeartbeatAck(payload: Uint8Array): void {
    try {
      this.publish(BRAIN_TOPICS_CONTROL.HEARTBEAT_ACK, payload);
    } catch (err) {
      console.error("[MqttInterface] Failed to handle heartbeat ping:", err);
    }
  }

  private sendEventAckIfNeeded(
    brainTopic: BrainTopic,
    envelope: Pick<Message, "typeName" | "payload">,
  ): void {
    if (!AUTO_ACK_TOPICS.has(brainTopic)) {
      return;
    }

    try {
      const header = extractIncomingHeader({
        typeName: envelope.typeName || BRAIN_TOPIC_TYPE_NAMES[brainTopic] || "",
        payload: envelope.payload,
      });
      if (!header) {
        return;
      }

      const ackPayload = encodeAckInfo(
        BRAIN_TOPICS_SYSTEM.REMOTE_ACK_INFO,
        header,
      );
      this.publish(BRAIN_TOPICS_SYSTEM.REMOTE_ACK_INFO, ackPayload);
    } catch (err) {
      console.error(
        `[MqttInterface] Failed to publish ACK for ${brainTopic}:`,
        err,
      );
    }
  }
}
